import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";

import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import useTeamPicker from "shared/composables/useTeamPicker";
import { safePush } from "shared/helpers/routing";
import { MediaRelease, MediaReleasesContactList } from "shared/resources";
import features from "shared/services/features";
import { useUserStore } from "shared/stores/user";

export function useCreateOutreachPitch() {
  const userStore = useUserStore();
  const { currentUser, organisation } = storeToRefs(userStore);

  const router = useRouter();
  const push = safePush({ router });

  function createOutreachPitch(data = {}) {
    const { selectedTeamPreference } = useTeamPicker();

    return new MediaRelease({
      userId: currentUser.value.id,
      organisationId: organisation.value.id,
      subject: "",
      message: "",
      status: "draft",
      fromName: `${currentUser.value.first_name} ${currentUser.value.last_name}`,
      replyTo: currentUser.value.email,
      mediaReleaseType: "pitch",
      personalised: true,
      ...(features.has("has_team_workspaces")
        ? { organisationTeamId: selectedTeamPreference.value }
        : {}),
      ...data,
    });
  }

  async function redirectToPitchEdit(pitch, prompt = "") {
    await push({
      name: "pitch-edit",
      params: { id: pitch.id },
      query: {
        autoGenerate: Boolean(prompt),
      },
    });
  }

  async function createOutreachPitchWithContactList(contactList, prompt = "") {
    if (contactList.contactListsContactsCount === 0) {
      throw new Error(
        getLocaleText("outreach_pitch.create_without_contacts_error")
      );
    }

    const pitch = createOutreachPitch({
      description: prompt,
      mediaReleasesContactLists: [
        new MediaReleasesContactList({
          contactListId: contactList.id,
        }),
      ],
    });

    await pitch.saveResource({
      with: "mediaReleasesContactLists",
    });

    await redirectToPitchEdit(pitch, prompt);
  }

  async function createOutreachPitchWithContacts(contactIds) {
    const pitch = createOutreachPitch();

    await pitch.saveResource();

    await $streemApiV1.post(
      `media_releases/${pitch.id}/add_media_release_recipients`,
      {
        params: {
          contact_keys: contactIds,
        },
      }
    );

    await redirectToPitchEdit(pitch);
  }

  return {
    createOutreachPitchWithContactList,
    createOutreachPitchWithContacts,
  };
}

export default useCreateOutreachPitch;
